/**
 * «Активность → Автоакцепт → Настройка правил» (`?view=accept&tab=rules`, право `accept_view`): кому какое
 * правило автоакцепта и почему — настройка HR, группа CRM 41 или роль. HR меняет правило в строке; в CRM не
 * пишет, своя таблица `accept_rule` («AI УЦ» #71). Логика — `review.откуда`, знание
 * `docs/knowledge/akcept-salona-pravila.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  type AcceptRuleRow,
  loadAcceptRules,
  retryServerErrors,
  RULE_LABEL,
  RULE_SOURCE,
  RULES,
  type Rule,
  setAcceptRule,
} from "@/lib/accept";

const ROLE_ORDER = ["стилист", "мастер", "комплектовщик", "руководитель"];

function когда(iso: string | null): string {
  if (!iso) return "";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
}

function Row({ p }: { p: AcceptRuleRow }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (rule: Rule | null) => setAcceptRule(p.user.id, rule),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accept-rules"] });
      void qc.invalidateQueries({ queryKey: ["activity-accept"] });
    },
  });
  return (
    <tr className="border-t border-border" data-annot={`правило «${p.user.name}»`}>
      <td className="px-4 py-2">
        <div className="font-medium text-fg">{p.user.name}</div>
        <div className="text-[11.5px] text-fg-faint">
          {p.location ?? "—"} · {p.role_name}
        </div>
      </td>
      <td className="px-2.5 py-2 text-fg-muted">{p.in_group ? "да" : <span className="text-fg-faint">—</span>}</td>
      <td className="px-2.5 py-2">
        <div className="text-fg">{RULE_LABEL[p.crm_rule]}</div>
        <div className="text-[11.5px] text-fg-faint">{RULE_SOURCE[p.crm_source]}</div>
      </td>
      <td className="px-2.5 py-2">
        <select
          value={p.hr?.rule ?? ""}
          disabled={m.isPending}
          onChange={(e) => m.mutate((e.target.value || null) as Rule | null)}
          aria-label={`Правило ${p.user.name}`}
          className={`h-8 rounded-[var(--radius-ui)] border bg-bg px-2 text-[12.5px] ${p.hr ? "border-accent text-fg" : "border-border text-fg-muted"}`}
        >
          <option value="">как по CRM — {RULE_LABEL[p.crm_rule]}</option>
          {RULES.map((r) => (
            <option key={r} value={r}>
              {RULE_LABEL[r]}
            </option>
          ))}
        </select>
        {m.isError && <div className="text-[11.5px] text-err">Не сохранилось: {String(m.error.message)}</div>}
      </td>
      <td className="px-2.5 py-2 text-[11.5px] text-fg-faint">
        {p.hr ? (
          <>
            <span className="text-fg-muted">HR</span>
            {p.hr.by_name ? ` · ${p.hr.by_name}` : ""}
            {p.hr.date ? ` · ${когда(p.hr.date)}` : ""}
            {p.hr.note ? <div>{p.hr.note}</div> : null}
          </>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

export function AcceptRulesView({ location, onLocation }: { location: string; onLocation: (location: string) => void }) {
  const q = useQuery({ queryKey: ["accept-rules"], queryFn: loadAcceptRules, retry: retryServerErrors });
  const [rule, setRule] = useState<Rule | "">("");
  const [name, setName] = useState("");

  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Читаю сотрудников и группы из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить правила: {String(q.error.message)}</div>;

  const d = q.data;
  const salons = [...new Map(d.people.filter((p) => p.location_id).map((p) => [String(p.location_id), p.location ?? "—"]))];
  const bySalon = location ? d.people.filter((p) => String(p.location_id) === location) : d.people;
  const people = bySalon.filter(
    (p) => (!rule || p.rule === rule) && (!name || p.user.name.toLowerCase().includes(name.trim().toLowerCase())),
  );
  const count = (r: Rule) => bySalon.filter((p) => p.rule === r).length;
  const hrCount = bySalon.filter((p) => p.hr).length;

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted" data-annot="логика правил">
        <div className="mb-2 font-medium text-fg">Как выбирается правило — первое подходящее</div>
        <ol className="flex list-decimal flex-col gap-1 pl-5">
          <li>
            <span className="text-fg">Настройка HR</span> — в строке ниже. Сильнее всего остального; «как по CRM» снимает её.
          </li>
          <li>
            <span className="text-fg">Группа CRM 41</span> «Должность / Полный акцепт» — «Полный акцепт».
          </li>
          <li>
            <span className="text-fg">По роли</span>:{" "}
            {Object.entries(d.by_role)
              .sort(([a], [b]) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b))
              .map(([role, r]) => `${role} — «${RULE_LABEL[r]}»`)
              .join(", ")}
            .
          </li>
        </ol>
        <div className="mt-3 grid gap-1.5">
          {RULES.map((r) => (
            <div key={r}>
              <span className="text-fg">«{RULE_LABEL[r]}»</span> — {d.rules[r].text}
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="отбор правил">
        <label className="flex items-center gap-2">
          Салон
          <select
            value={location}
            onChange={(e) => onLocation(e.target.value)}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          >
            <option value="">все салоны</option>
            {salons.map(([id, n]) => (
              <option key={id} value={id}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="имя сотрудника"
          aria-label="Имя сотрудника"
          className="h-8 w-[200px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
        />
        <div className="flex items-center gap-1" data-annot="правило">
          {(["", ...RULES] as (Rule | "")[]).map((r) => (
            <button
              key={r || "all"}
              type="button"
              onClick={() => setRule(r)}
              aria-pressed={rule === r}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${rule === r ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {r ? `${RULE_LABEL[r]} ${count(r)}` : `все ${bySalon.length}`}
            </button>
          ))}
        </div>
        <span className="text-fg-faint">настроено HR: {hrCount}</span>
      </div>

      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="сотрудники и правила">
        {people.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">Никого не нашлось.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">Сотрудник</th>
                  <th className="px-2.5 py-2 font-medium" title="состоит в группе CRM 41 «Должность / Полный акцепт»">
                    Группа 41
                  </th>
                  <th className="px-2.5 py-2 font-medium" title="правило без настройки HR: группа CRM 41 или по роли">
                    По CRM
                  </th>
                  <th className="px-2.5 py-2 font-medium">Правило</th>
                  <th className="px-2.5 py-2 font-medium">Кто задал</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <Row key={p.user.id} p={p} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
